import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Stack, Button, TextField, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Checkbox, CircularProgress, Alert, Stepper, Step, StepLabel, Divider
} from '@mui/material';
import { School as SchoolIcon, Upgrade as UpgradeIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const PromotionWizardPage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    // Data
    const [groups, setGroups] = useState<any[]>([]);
    const [students, setStudents] = useState<any[]>([]);
    
    // Selections
    const [sourceGroupId, setSourceGroupId] = useState('');
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    
    // Target
    const [isGraduating, setIsGraduating] = useState(false);
    const [targetGrade, setTargetGrade] = useState('');
    const [targetClass, setTargetClass] = useState('');
    const [disconnectOld, setDisconnectOld] = useState(true);

    const steps = ['Select Source Class', 'Review Students', 'Set Destination', 'Confirm & Execute'];

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('groups/my'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGroups(res.data);
        } catch (error) {
            console.error('Failed to fetch groups', error);
        }
    };

    const fetchStudentsForGroup = async (groupId: string) => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`groups/${groupId}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStudents(res.data.students || []);
            setSelectedStudentIds((res.data.students || []).map((s: any) => s.id));
        } catch (error) {
            console.error('Failed to fetch students', error);
            setMessage({ type: 'error', text: 'Failed to fetch students for this class.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSourceGroupChange = (e: any) => {
        const val = e.target.value;
        setSourceGroupId(val);
        if (val) {
            fetchStudentsForGroup(val);
        } else {
            setStudents([]);
            setSelectedStudentIds([]);
        }
    };

    const handleToggleStudent = (id: string) => {
        setSelectedStudentIds(prev => 
            prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]
        );
    };

    const handleNext = () => {
        setMessage({ type: '', text: '' });
        
        if (activeStep === 0 && !sourceGroupId) {
            return setMessage({ type: 'warning', text: 'Please select a source class first.' });
        }
        if (activeStep === 1 && selectedStudentIds.length === 0) {
            return setMessage({ type: 'warning', text: 'Please select at least one student.' });
        }
        if (activeStep === 2 && !isGraduating && (!targetGrade || !targetClass)) {
            return setMessage({ type: 'warning', text: 'Please specify the target grade and class, or choose graduation.' });
        }
        
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const executePromotion = async () => {
        try {
            setLoading(true);
            setMessage({ type: '', text: '' });
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            
            await axios.post(getApiUrl('students/promote'), {
                studentIds: selectedStudentIds,
                markAsAlumni: isGraduating,
                targetGrade: isGraduating ? null : targetGrade,
                targetClass: isGraduating ? null : targetClass,
                disconnectFromGroups: disconnectOld
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setMessage({ type: 'success', text: `Successfully processed ${selectedStudentIds.length} students!` });
            setActiveStep(4); // Finished state
        } catch (error: any) {
            console.error(error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to promote students' });
        } finally {
            setLoading(false);
        }
    };

    const resetWizard = () => {
        setActiveStep(0);
        setSourceGroupId('');
        setStudents([]);
        setSelectedStudentIds([]);
        setIsGraduating(false);
        setTargetGrade('');
        setTargetClass('');
        setMessage({ type: '', text: '' });
    };

    const sourceGroup = groups.find(g => g.id === sourceGroupId);

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
            <Typography variant="h4" fontWeight={900} color="#1A202C" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <UpgradeIcon fontSize="large" color="primary" /> Student Promotion & Roll-Over Wizard
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
                Move an entire class to the next grade or graduate them to Alumni status.
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
                {steps.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>

            {message.text && (
                <Alert severity={message.type as any} sx={{ mb: 4, borderRadius: '12px', fontWeight: 600 }}>
                    {message.text}
                </Alert>
            )}

            <Paper sx={{ p: 4, borderRadius: '24px', minHeight: 400, border: '1px solid #edf2f7', display: 'flex', flexDirection: 'column' }}>
                {loading && activeStep !== 4 ? (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ flex: 1 }}>
                        {/* STEP 1: Source */}
                        {activeStep === 0 && (
                            <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'center', py: 5 }}>
                                <SchoolIcon sx={{ fontSize: 60, color: '#A0AEC0', mb: 2 }} />
                                <Typography variant="h6" fontWeight={800} mb={3}>Which class is being promoted?</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    label="Source Class Group"
                                    value={sourceGroupId}
                                    onChange={handleSourceGroupChange}
                                >
                                    {groups.map(g => (
                                        <MenuItem key={g.id} value={g.id}>
                                            {g.name} (Grade: {g.grade})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}

                        {/* STEP 2: Students */}
                        {activeStep === 1 && (
                            <Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={2}>
                                    <Typography variant="h6" fontWeight={800}>Review Students in {sourceGroup?.name}</Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                        Selected: {selectedStudentIds.length} / {students.length}
                                    </Typography>
                                </Stack>
                                
                                <TableContainer sx={{ maxHeight: 400, border: '1px solid #edf2f7', borderRadius: '12px' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox 
                                                        checked={selectedStudentIds.length === students.length && students.length > 0}
                                                        indeterminate={selectedStudentIds.length > 0 && selectedStudentIds.length < students.length}
                                                        onChange={(e) => setSelectedStudentIds(e.target.checked ? students.map(s => s.id) : [])}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Student ID</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Current Grade</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {students.map(student => (
                                                <TableRow key={student.id}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox 
                                                            checked={selectedStudentIds.includes(student.id)}
                                                            onChange={() => handleToggleStudent(student.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{student.studentId}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>{student.firstName} {student.lastName}</TableCell>
                                                    <TableCell>{student.grade} - {student.class}</TableCell>
                                                </TableRow>
                                            ))}
                                            {students.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No students found in this group.</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {/* STEP 3: Target */}
                        {activeStep === 2 && (
                            <Box sx={{ maxWidth: 600, mx: 'auto', py: 3 }}>
                                <Typography variant="h6" fontWeight={800} mb={3}>Where are they going?</Typography>
                                
                                <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: '16px', borderColor: isGraduating ? '#E2E8F0' : '#4C51BF', bgcolor: isGraduating ? 'transparent' : '#EBF4FF' }}>
                                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                        <Checkbox checked={!isGraduating} onChange={() => setIsGraduating(false)} />
                                        <Typography variant="subtitle1" fontWeight={700}>Promote to Next Grade</Typography>
                                    </Stack>
                                    <Grid container spacing={2} sx={{ pl: 6, opacity: isGraduating ? 0.5 : 1, pointerEvents: isGraduating ? 'none' : 'auto' }}>
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Target Grade (e.g., Grade 6)"
                                                value={targetGrade}
                                                onChange={e => setTargetGrade(e.target.value)}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Target Class (e.g., A)"
                                                value={targetClass}
                                                onChange={e => setTargetClass(e.target.value)}
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', borderColor: isGraduating ? '#48BB78' : '#E2E8F0', bgcolor: isGraduating ? '#F0FFF4' : 'transparent' }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Checkbox checked={isGraduating} onChange={() => setIsGraduating(true)} />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700} color={isGraduating ? '#22543D' : 'text.primary'}>Graduate (Terminal Grade)</Typography>
                                            <Typography variant="body2" color="text.secondary">Use this for Grade 7, Form 4 (leaving), or Form 6. Status will become ALUMNI.</Typography>
                                        </Box>
                                    </Stack>
                                </Paper>

                                <Divider sx={{ my: 4 }} />
                                
                                <Typography variant="subtitle2" fontWeight={800} mb={2}>Background Actions</Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Checkbox checked={disconnectOld} onChange={e => setDisconnectOld(e.target.checked)} />
                                    <Typography variant="body2" fontWeight={600}>Disconnect from old Class Group (Cleans up attendance views for the new year)</Typography>
                                </Stack>
                            </Box>
                        )}

                        {/* STEP 4: Confirm */}
                        {activeStep === 3 && (
                            <Box sx={{ textAlign: 'center', py: 5, maxWidth: 500, mx: 'auto' }}>
                                <Typography variant="h5" fontWeight={900} mb={2}>Ready to Execute</Typography>
                                <Alert severity="info" sx={{ mb: 4, borderRadius: '12px' }}>
                                    You are about to process {selectedStudentIds.length} students from {sourceGroup?.name}.
                                </Alert>
                                
                                <Paper sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: '16px', textAlign: 'left', mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight={800} mb={1}>ACTION SUMMARY</Typography>
                                    {isGraduating ? (
                                        <Typography variant="body1" fontWeight={700} color="#E53E3E">🎓 Status will change to ALUMNI</Typography>
                                    ) : (
                                        <Typography variant="body1" fontWeight={700} color="#4C51BF">➡️ Move to {targetGrade} - {targetClass}</Typography>
                                    )}
                                    {disconnectOld && (
                                        <Typography variant="body2" color="text.secondary" mt={1}>• Remove from current class group bindings</Typography>
                                    )}
                                </Paper>
                                
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    Note: Historical attendance and report cards are permanently preserved.
                                </Typography>
                            </Box>
                        )}

                        {/* Done */}
                        {activeStep === 4 && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <CheckCircleIcon sx={{ fontSize: 80, color: '#48BB78', mb: 2 }} />
                                <Typography variant="h5" fontWeight={900} mb={1}>Operation Complete</Typography>
                                <Typography variant="body1" color="text.secondary" mb={4}>
                                    The students have been successfully processed.
                                </Typography>
                                <Button variant="outlined" onClick={resetWizard} sx={{ borderRadius: '12px', fontWeight: 700 }}>
                                    Start Another Promotion
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Footer Nav */}
                {activeStep < 4 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #edf2f7' }}>
                        <Button 
                            disabled={activeStep === 0 || loading} 
                            onClick={handleBack}
                            sx={{ fontWeight: 700 }}
                        >
                            Back
                        </Button>
                        {activeStep === 3 ? (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={executePromotion} 
                                disabled={loading}
                                sx={{ borderRadius: '8px', fontWeight: 800, px: 4 }}
                            >
                                Execute
                            </Button>
                        ) : (
                            <Button 
                                variant="contained" 
                                onClick={handleNext}
                                sx={{ borderRadius: '8px', fontWeight: 800, px: 4 }}
                            >
                                Next Step
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default PromotionWizardPage;
